import { useCallback, useEffect, useState } from 'react';
import { getFirebaseAuth } from '../../utils/firebase/firebase';
import { useAuth } from './useAuth';

interface UseAuthTokenResult {
    token: string | null;
    loading: boolean;
    error: Error | null;
    getToken: (forceRefresh?: boolean) => Promise<string | null>;
}

/**
 * Hook to get the current user's authentication token
 * @param forceRefresh - Whether to force a token refresh
 */
export const useAuthToken = (forceRefresh = false): UseAuthTokenResult => {
    const { user } = useAuth();
    const [token, setToken] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<Error | null>(null);

    const getToken = useCallback(
        async (refresh = false) => {
            if (!user) {
                setToken(null);
                setLoading(false);
                return null;
            }

            try {
                setLoading(true);
                const idToken = await getFirebaseAuth().currentUser?.getIdToken(refresh);
                setToken(idToken || null);
                return idToken || null;
            } catch (err) {
                setError(err as Error);
                return null;
            } finally {
                setLoading(false);
            }
        },
        [user],
    );

    useEffect(() => {
        getToken(forceRefresh);
    }, [getToken, forceRefresh]);

    return { token, loading, error, getToken };
};

/**
 * Example usage:
 *
 * // In a component
 * const { token, loading, getToken } = useAuthToken();
 *
 * // Force refresh token
 * const handleRefreshToken = async () => {
 *   const freshToken = await getToken(true);
 *   console.log('Fresh token:', freshToken);
 * };
 *
 * // Use token in API request
 * const fetchData = async () => {
 *   if (!token) return;
 *
 *   const response = await fetch('/api/data', {
 *     headers: {
 *       Authorization: `Bearer ${token}`,
 *     },
 *   });
 *
 *   const data = await response.json();
 *   console.log('Data:', data);
 * };
 */
